<template>
	<div v-if="isApproved" class="c-admin-api-access-page">
		<PageHeader title="Admin">
			<template v-slot:title>API access</template>
		</PageHeader>
		<Constrain>
			<AdminNav />
			<section class="c-admin-api-access-page__card" :aria-busy="loading">
			<h2>Public API calls</h2>
			<form class="c-admin-api-access-page__search" @submit.prevent="searchWorkspace">
				<div class="form-group">
					<label for="api-access-workspace-id">Workspace ID</label>
					<input
						id="api-access-workspace-id"
						v-model.trim="workspaceInput"
						type="text"
						inputmode="numeric"
						class="form-control"
						placeholder="Enter workspace ID"
					/>
				</div>
				<button type="submit" class="btn btn-primary" :disabled="loading">Search</button>
				<button type="button" class="btn" :disabled="loading" @click="showUnknown">
					Unknown workspace
				</button>
			</form>
			<p v-if="errorMessage" role="alert">{{ errorMessage }}</p>
			<p v-if="loading" role="status">Loading API calls...</p>
			<template v-else-if="result">
				<h3>{{ selectedLabel }}</h3>
				<p>
					{{ result.startDate }} – {{ result.endDate }} (UTC). First and current days are partial.
				</p>
				<p>
					<strong>{{ successTotal + failureTotal }}</strong> calls in 180 days:
					{{ successTotal }} successful, {{ failureTotal }} failed.
				</p>
				<AdminApiAccessChart :days="result.days" />
				<h3>Latest 100 calls</h3>
				<p v-if="result.calls.length === 0" role="status">No API calls found.</p>
				<div v-else class="c-admin-api-access-page__table-scroll">
					<table>
						<thead>
							<tr>
								<th scope="col">Time (UTC)</th>
								<th scope="col">Call</th>
								<th scope="col">Status</th>
								<th scope="col">API key</th>
								<th scope="col">User agent</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="call in result.calls" :key="call.id">
								<td>{{ formatTime(call.createdAt) }}</td>
								<td>{{ call.method }} {{ call.path }}</td>
								<td>{{ call.statusCode }} {{ call.success ? 'Success' : 'Failure' }}</td>
								<td>{{ call.apiKey || '—' }}</td>
								<td>{{ call.userAgent || '—' }}</td>
							</tr>
						</tbody>
					</table>
				</div>
			</template>
		</section>
		</Constrain>
	</div>
	<Page404 v-else />
</template>

<script>
import { http } from '@swipekit/lib/http';
import Constrain from '@swipekit/components/utils/constrain.vue';
import PageHeader from '@swipekit/components/utils/page-header.vue';
import Page404 from './page404.vue';
import AdminNav from './admin-nav.vue';
import AdminApiAccessChart from './admin-api-access-chart.vue';

export default {
	name: 'AdminApiAccessPage',
	components: { Constrain, PageHeader, Page404, AdminNav, AdminApiAccessChart },
	data() {
		return {
			workspaceInput: '',
			selectedLabel: '',
			result: null,
			loading: false,
			errorMessage: '',
			requestId: 0,
		};
	},
	computed: {
		isApproved() {
			const user = this.$store.getters['users/user'];
			return !!(user && user.email && user.email.toLowerCase() === 'shash@swipekit.app');
		},
		successTotal() {
			return this.result ? this.result.days.reduce((total, day) => total + day.successCount, 0) : 0;
		},
		failureTotal() {
			return this.result ? this.result.days.reduce((total, day) => total + day.failureCount, 0) : 0;
		},
	},
	watch: {
		isApproved(approved) {
			if (!approved) {
				this.requestId++;
				this.result = null;
			}
		},
	},
	methods: {
		searchWorkspace() {
			const id = this.workspaceInput;
			if (!/^[1-9]\d*$/.test(id) || Number(id) > 2147483647) {
				this.errorMessage = 'Enter a valid workspace ID.';
				return;
			}
			this.selectedLabel = `Workspace ${id}`;
			this.loadCalls({ workspaceId: id });
		},
		showUnknown() {
			this.selectedLabel = 'Unknown workspace';
			this.loadCalls({ scope: 'unknown' });
		},
		async loadCalls(params) {
			if (!this.isApproved) return;
			const requestId = ++this.requestId;
			this.result = null;
			this.loading = true;
			this.errorMessage = '';
			try {
				const response = await http.get('/admin/api-access', { params });
				if (this.isApproved && requestId === this.requestId) {
					this.result = response.data;
				}
			} catch (err) {
				if (requestId === this.requestId) {
					this.errorMessage = 'Could not load API calls. Try again.';
				}
			} finally {
				if (requestId === this.requestId) {
					this.loading = false;
				}
			}
		},
		formatTime(value) {
			return value ? value.replace('T', ' ').slice(0, 19) : '';
		},
	},
};
</script>

<style lang="scss">
.c-admin-api-access-page {
	padding-bottom: var(--spacer);

	&__card {
		padding: var(--spacer-sm);
		border: 1px solid var(--color-bg-5);
		border-radius: 0.5rem;
		background: var(--color-bg-2);
	}

	&__search {
		display: flex;
		align-items: flex-end;
		flex-wrap: wrap;
		gap: var(--margin-sm);
		margin: var(--margin-lg) 0;

		.form-group {
			min-width: 180px;
			margin: 0;
		}
	}

	&__table-scroll {
		overflow-x: auto;
	}

	table {
		width: 100%;
		border-collapse: collapse;
		font-size: var(--font-size-sm);
	}

	th,
	td {
		padding: var(--margin);
		border-bottom: 1px solid var(--color-bg-5);
		text-align: left;
		vertical-align: top;
		overflow-wrap: anywhere;
	}

	th:first-child,
	td:first-child {
		min-width: 145px;
	}
}
</style>
